import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import FieldRow from "@/components/mobile/FieldRow";
import { MATCH_STATUS } from "../types/invoice";
import type { MatchResult } from "../types/invoice";
import { V2_COLORS } from "../constants/colors";
import { getInvoiceValueDelta } from "../utils/invoiceValueDelta";

interface ThreeWayMatchCardProps {
  matchResult: MatchResult | null;
  poAmount?: number | null;
  invoiceAmount?: number | null;
  grnAmount?: number | null;
  isFins?: boolean;
  /** Mobile (< md): render the comparison as stacked FieldRows instead of a table. */
  isMobile?: boolean;
}

// --- Styles ---

const thStyle = {
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "16px",
  color: V2_COLORS.textSecondary,
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  bgcolor: "#F2F2F2",
  borderBottomWidth: 2,
  borderColor: "#E6E6E6",
  py: 1,
  px: 3,
};

const tdStyle = {
  fontSize: "14px",
  lineHeight: "20px",
  py: 1.5,
  px: 3,
  borderColor: "#E6E6E6",
};

const CARD_SX = { bgcolor: "background.paper", borderRadius: "8px", py: 2, pl: 1.5, pr: 3 };
const CARD_SX_MOBILE = { bgcolor: "background.paper", borderRadius: "8px", p: 1.5 };

// --- Formatting helpers ---

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return "—";
  return `₹${val.toLocaleString("en-IN")}`;
}

// --- Result tag ---

function ResultTag({ pass }: { pass: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        pl: "4px",
        pr: "8px",
        py: "4px",
        borderRadius: "4px",
        color: pass ? "#387E47" : "#AF2D22",
        backgroundColor: pass ? "#EDF5EE" : "#FBEBEA",
        border: `1px solid ${pass ? "#DAECDE" : "#F8D7D4"}`,
        whiteSpace: "nowrap",
      }}
    >
      {pass ? <Check sx={{ fontSize: 12 }} /> : <Close sx={{ fontSize: 12 }} />}
      <Typography
        component="span"
        sx={{
          color: "inherit",
          fontSize: "10px",
          fontWeight: 500,
          lineHeight: "12px",
          textTransform: "uppercase",
        }}
      >
        {pass ? "Match" : "Mismatch"}
      </Typography>
    </Box>
  );
}

// --- Component ---

export default function ThreeWayMatchCard({
  matchResult,
  poAmount,
  invoiceAmount,
  grnAmount,
  isFins = false,
  isMobile = false,
}: ThreeWayMatchCardProps) {
  if (!matchResult) {
    return (
      <Box>
        <Typography fontWeight={700} fontSize="16px" lineHeight="24px" mb={1.5}>
          3 Way Match
        </Typography>
        <Box sx={isMobile ? CARD_SX_MOBILE : CARD_SX}>
          <Typography fontSize="14px" color="textSecondary" py={2} textAlign="center">
            Match result not available yet
          </Typography>
        </Box>
      </Box>
    );
  }

  const passed = matchResult.match_status === MATCH_STATUS.SUCCESS;
  // Amount row result: UI-side tolerance check (mirrors backend absolute tolerance).
  // FINS = two-way (PO vs Invoice only); non-FINS = three-way (PO vs Invoice AND Invoice vs GRN).
  // amountsKnown guard: if any required amount is absent, we cannot confirm a match —
  // fall back to Mismatch rather than producing a false-positive green tag.
  const AMOUNT_TOLERANCE = getInvoiceValueDelta();
  const amountsKnown = poAmount != null && invoiceAmount != null && (isFins || grnAmount != null);
  const poInvoiceMatch = Math.abs((poAmount ?? 0) - (invoiceAmount ?? 0)) <= AMOUNT_TOLERANCE;
  const invoiceGrnMatch = isFins || Math.abs((invoiceAmount ?? 0) - (grnAmount ?? 0)) <= AMOUNT_TOLERANCE;
  const amountPass = amountsKnown && poInvoiceMatch && invoiceGrnMatch;

  const notes = matchResult.notes
    ? matchResult.notes
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  return (
    <Box>
      <Typography fontWeight={700} fontSize="16px" lineHeight="24px" mb={1.5}>
        3 Way Match
      </Typography>
      <Box sx={isMobile ? CARD_SX_MOBILE : CARD_SX}>
        {isMobile ? (
          <Box display="flex" flexDirection="column" gap={1} px={0.5}>
            <Typography
              fontSize="12px"
              fontWeight={700}
              lineHeight="16px"
              color={V2_COLORS.textSecondary}
              textTransform="uppercase"
            >
              Amount
            </Typography>
            <FieldRow label="PO" value={formatCurrency(poAmount)} />
            <FieldRow label="Invoice" value={formatCurrency(invoiceAmount)} />
            {!isFins && <FieldRow label="GRN" value={formatCurrency(grnAmount)} />}
            <FieldRow label="Result" value={<ResultTag pass={amountPass} />} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thStyle, width: 100 }}>Field</TableCell>
                  <TableCell sx={thStyle}>PO</TableCell>
                  <TableCell sx={thStyle}>Invoice</TableCell>
                  {!isFins && <TableCell sx={thStyle}>GRN</TableCell>}
                  <TableCell sx={thStyle}>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={tdStyle}>Amount</TableCell>
                  <TableCell sx={tdStyle}>{formatCurrency(poAmount)}</TableCell>
                  <TableCell sx={tdStyle}>{formatCurrency(invoiceAmount)}</TableCell>
                  {!isFins && <TableCell sx={tdStyle}>{formatCurrency(grnAmount)}</TableCell>}
                  <TableCell sx={tdStyle}>
                    <ResultTag pass={amountPass} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary banner */}
        <Box
          sx={{
            mt: 1.5,
            p: 1,
            backgroundColor: passed ? "#EDF5EE" : "#FBEBEA",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: passed ? "#387E47" : "#AF2D22",
            borderRadius: "4px",
          }}
        >
          {passed ? <Check sx={{ fontSize: 14 }} /> : <Close sx={{ fontSize: 14 }} />}
          <Typography fontWeight={600} fontSize="14px" lineHeight="20px" color="inherit">
            {passed
              ? "3-Way match passed"
              : "3-Way match failed - exceptions found"}
          </Typography>
        </Box>

        {/* Notes — shown only when match_status !== "SUCCESS" */}
        {!passed && notes.length > 0 && (
          <Box sx={{ mt: 1.5, px: 1 }}>
            {notes.map((note, i) => (
              <Typography key={i} fontSize="12px" color="textSecondary" lineHeight="18px">
                &bull; {note}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
